/// <reference path="../../js/_ResizeObserver.ts" />
import Component from '../../js/_Component';
import Modal from '../../js/util/_Modal';

export default class ImageMosaic extends Component {
	private tileContainer: HTMLElement;

	private textContainer: HTMLElement;

	private tiles: NodeListOf<HTMLElement>;

	private columns: number;

	private positions: object;

	private minYOffset: number;

	private maxYOffset: number;

	private modalTemplate: HTMLTemplateElement;

	constructor(element: HTMLElement) {
		super(element);

		/** The div which contains the tiles. */
		this.tileContainer = element.querySelector('.c-image-mosaic__container');

		/** The div which contains the text. */
		this.textContainer = element.querySelector('.c-image-mosaic__text');

		/** The divs which have background images. */
		this.tiles = element.querySelectorAll('.c-image-mosaic__tile');

		/** The hidden template which holds the content to use in the modal. */
		this.modalTemplate = element.querySelector('.c-image-mosaic__modal');

		/** The direct children of the modal template. */
		const modalPanels = this.modalTemplate.content.children;

		// Attach a click handler to each tile that will create and open a modal.
		Array.from(this.tiles).forEach((tile, index) => {
			tile.addEventListener('click', () => {
				// Clone each modal panel.
				const panelArray = Array.from(modalPanels)
					.map((modalPanel) => modalPanel.cloneNode(true) as HTMLElement);

				// Create a new modal with the array of clones.
				const modal = new Modal(panelArray);

				// Open the modal at the index of the item that was clicked.
				modal.showPanel(index);
			});
		});

		/** The number of columns that the tile container is broken in to. */
		this.columns = 3;

		/** The map of tile indeces to X/Y offsets, where 1 is the size of a tile. */
		this.positions = {
			0: {x: -1, y: 1.5},
			1: {x: 0, y: 0},
			2: {x: 0, y: 1},
			3: {x: 0, y: 2},
			4: {x: 1, y: 0.5},
			5: {x: 1, y: 1.5},
			6: {x: 2, y: -0.75},
			7: {x: 2, y: 0.25},
			8: {x: 2, y: 1.25},
			9: {x: 2, y: 2.25},
			10: {x: 3, y: 0.125},
		};

		/** The minimum Y offset found in the positions map. */
		this.minYOffset = Object.keys(this.positions).reduce((min, tile, i) => (
			(min === undefined || this.positions[i].y < min) ? this.positions[i].y : min
		), 0);

		/** The maximum Y offset found in the positions map. */
		this.maxYOffset = Object.keys(this.positions).reduce((max, tile, i) => (
			(max === undefined || this.positions[i].y > max) ? this.positions[i].y : max
		), 0);

		this.setAllTileSizes();
		this.setLayout();
		const resizeObserver = new ResizeObserver(() => this.setLayout());
		resizeObserver.observe(this.element);
	}

	/** Sets the width and height of a tile. */
	static setTileSize(tile, size) {
		tile.style.width = `${size}%`;
		tile.style.paddingTop = `${size}%`;
	}

	/** Sets the width and height of all tiles. */
	setAllTileSizes() {
		for (let i = 0; i < this.tiles.length; i += 1) {
			ImageMosaic.setTileSize(this.tiles[i], 100 / this.columns);
		}
	}

	/** Sets the transform value for a tile. */
	static setTilePosition(tile, size, x, y) {
		tile.style.transform = `translate(${size * x}px, ${size * y}px)`;
	}

	/** Sets the transform values for all tiles. */
	setAllTilePositions(size) {
		for (let i = 0; i < this.tiles.length; i += 1) {
			ImageMosaic.setTilePosition(this.tiles[i], size, this.positions[i].x, this.positions[i].y);
		}
	}

	/** Calculates the minimum height for the text so it can occupy the full gap above the tiles. */
	getTextMinHeight(size) {
		return Math.abs(this.minYOffset * size);
	}

	/** Sets the minimum height for the text. */
	setTextMinHeight(size) {
		this.textContainer.style.minHeight = `${this.getTextMinHeight(size)}px`;
	}

	/** Calculates the full height of the visual bounding box of the tile container. */
	getTileContainerHeight(size) {
		const highestOffset = this.minYOffset * size;
		const lowestOffset = this.maxYOffset * size;
		const textContainerHeight = this.textContainer.getBoundingClientRect().height;
		return lowestOffset - Math.min(highestOffset, -textContainerHeight) + size;
	}

	/** Sets the height for the tile container. */
	setTileContainerHeight(size) {
		this.tileContainer.style.height = `${this.getTileContainerHeight(size)}px`;
	}

	/** Updates all tile positions and the tile container height. */
	setLayout() {
		/** The width of one tile. */
		const size = this.tileContainer.getBoundingClientRect().width / this.columns;

		window.requestAnimationFrame(() => {
			this.setAllTilePositions(size);
			this.setTextMinHeight(size);
			this.setTileContainerHeight(size);
		});
	}
}

Component.register(ImageMosaic, 'ImageMosaic');
